// Task #2892 — Card "Meta Business" (Dash Integrazioni).
//
// Superficie SEPARATA dal preset MCP `meta-ads`: connessione Facebook Login for
// Business per-tenant + per-utente via `/api/meta/*`. La config dell'app Meta è
// centralizzata nel Master; qui l'utente avvia il consenso (popup) e gestisce le
// Pagine collegate.
//
// Modello multi-pagina gerarchico: Account (connessione utente, token
// long-lived) → Business Manager → Pagine (`tenant_meta_pages`). Il dialog
// mostra un accordion a 2 livelli: riga per Account → espande i BM → espande le
// Pagine (toggle "Attiva per chat" + badge capability derivati dagli scope della
// connessione, read-only perché governati dal config_id Meta centralizzato).

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader2, RotateCw, CheckCircle, AlertTriangle, Facebook, Instagram, MessageCircle, Building2, UserPlus, Settings, ChevronRight, ChevronDown, KeyRound, Pencil, Trash2 } from 'lucide-react';
import type {
  MetaConnectionStatus,
  MetaPageStatus,
  MetaPageWebhookStatus,
  MetaRealignResponse,
  MetaStatusResponse,
  MetaConnectionRefreshTokenResponse,
  MetaConnectionDeleteResponse,
  MetaConnectionLabelResponse,
} from '@easyflow/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import { getErrorMessage } from '../../lib/utils';

// Capability derivate dagli scope concessi alla connessione (read-only).
interface Capability {
  key: string;
  label: string;
  scope: string;
}

const CAPABILITIES: Capability[] = [
  { key: 'messenger', label: 'Messenger', scope: 'pages_messaging' },
  { key: 'instagram', label: 'Instagram DM', scope: 'instagram_manage_messages' },
  { key: 'business', label: 'Business', scope: 'business_management' },
];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('it-IT');
}

function isExpired(iso: string | null): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
}

interface BusinessGroup {
  businessKey: string;
  businessName: string;
  pages: MetaPageStatus[];
}

interface AccountGroup {
  connection: MetaConnectionStatus;
  businesses: BusinessGroup[];
  pageCount: number;
}

// ---------------------------------------------------------------------------
// Badge stato per-account (usato nell'accordion riga account)
// ---------------------------------------------------------------------------

interface ConnectionStatusBadgesProps {
  conn: MetaConnectionStatus;
  tokenExpired: boolean;
}

/** Render del badge/i di stato di una connessione Meta.
 *  - `permission_error` → badge ambra "Permessi mancanti"
 *  - `expired` o token scaduto per data → badge rosso "Token scaduto" con
 *    tooltip che mostra lastRefreshError (motivo del fallimento) se presente
 *  - `connected` con lastRefreshError valorizzato → badge verde "Connesso" +
 *    badge secondario ambra "Errore sincronizzazione" con tooltip del motivo
 *  - `connected` senza errori → solo badge verde
 *  - altri valori → badge outline con il valore grezzo */
function ConnectionStatusBadges({ conn, tokenExpired }: ConnectionStatusBadgesProps) {
  if (conn.status === 'permission_error') {
    return (
      <Badge variant="outline" className="shrink-0 border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
        Permessi mancanti
      </Badge>
    );
  }

  if (tokenExpired || conn.status === 'expired') {
    const badge = (
      <Badge variant="outline" className="shrink-0 border-red-200 bg-red-50 text-red-600 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
        Token scaduto
      </Badge>
    );
    if (!conn.lastRefreshError) return badge;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent className="max-w-xs break-words">{conn.lastRefreshError}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (conn.status === 'connected') {
    const connectedBadge = (
      <Badge className="shrink-0 bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">Connesso</Badge>
    );
    if (!conn.lastRefreshError) return connectedBadge;
    // Token valido ma si è verificato un errore tecnico nell'ultimo ciclo.
    return (
      <div className="flex shrink-0 items-center gap-1">
        {connectedBadge}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="shrink-0 border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                <AlertTriangle className="mr-1 h-3 w-3" />
                Errore sincronizzazione
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs break-words">{conn.lastRefreshError}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return <Badge variant="outline" className="shrink-0">{conn.status}</Badge>;
}

export default function MetaBusinessCard() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [appAvailable, setAppAvailable] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [connections, setConnections] = useState<MetaConnectionStatus[]>([]);
  const [pages, setPages] = useState<MetaPageStatus[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [togglingPageId, setTogglingPageId] = useState<string | null>(null);
  // Stato accordion: chiavi connectionId (account) e `${connId}::${bizKey}` (BM).
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const [expandedBusinesses, setExpandedBusinesses] = useState<Set<string>>(new Set());
  const didInitExpand = useRef(false);
  const [realigning, setRealigning] = useState(false);
  // Azione per-account in corso: `${connId}:${azione}` (una sola alla volta).
  const [rowAction, setRowAction] = useState<string | null>(null);
  // Dialog rinomina/elimina per-account.
  const [renameTarget, setRenameTarget] = useState<MetaConnectionStatus | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<MetaConnectionStatus | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await apiClient.get<MetaStatusResponse>('/meta/status');
      if (res.success && res.data) {
        setAppAvailable(res.data.meta_app_available);
        setCurrentUserId(res.data.current_user_id);
        setConnections(res.data.connections ?? []);
        setPages(res.data.pages ?? []);
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  // Flusso popup OAuth condiviso tra "Collega/Aggiungi utente" (connect) e
  // "Modifica autorizzazioni" (edit). `query` instrada start verso la modalità.
  const runOAuthPopup = useCallback(
    async (query: string, successMessage: string) => {
      setConnecting(true);
      try {
        const res = await apiClient.get<{ authUrl: string; state: string }>(`/meta/oauth/start${query}`);
        if (!res.success || !res.data?.authUrl) {
          throw new Error(res.error || 'Avvio connessione non riuscito');
        }
        const popup = window.open(res.data.authUrl, 'meta-business-oauth', 'width=600,height=720');
        if (!popup) {
          throw new Error('Popup bloccato dal browser. Abilita i popup e riprova.');
        }

        const onMessage = (event: MessageEvent) => {
          const data = event.data as {
            source?: string;
            status?: string;
            error?: string;
          } | null;
          if (!data || data.source !== 'meta-business-oauth') return;
          window.removeEventListener('message', onMessage);
          setConnecting(false);
          if (data.status === 'success') {
            toast({ title: 'Operazione completata', description: successMessage });
            void loadStatus();
          } else {
            toast({ title: 'Operazione non riuscita', description: data.error || 'Errore OAuth', variant: 'destructive' });
          }
        };
        window.addEventListener('message', onMessage);

        // Safety net: popup chiuso senza messaggio → ricarica stato.
        const poll = window.setInterval(() => {
          if (popup.closed) {
            window.clearInterval(poll);
            window.removeEventListener('message', onMessage);
            setConnecting(false);
            void loadStatus();
          }
        }, 1000);
      } catch (err) {
        setConnecting(false);
        toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
      }
    },
    [toast, loadStatus]
  );

  const handleConnect = useCallback(() => {
    void runOAuthPopup('', 'Account Meta Business collegato.');
  }, [runOAuthPopup]);

  const handleEditPermissions = useCallback(
    (connectionId: string) =>
      runOAuthPopup(
        `?mode=edit&connection_id=${encodeURIComponent(connectionId)}`,
        'Autorizzazioni Meta aggiornate.'
      ),
    [runOAuthPopup]
  );

  const handleToggleChat = useCallback(
    async (page: MetaPageStatus, next: boolean) => {
      setTogglingPageId(page.pageId);
      // Aggiornamento ottimistico.
      setPages((prev) => prev.map((p) => (p.pageId === page.pageId ? { ...p, chatEnabled: next } : p)));
      try {
        const res = await apiClient.patch<{ page_id: string; chat_enabled: boolean }>(
          `/meta/pages/${encodeURIComponent(page.pageId)}`,
          { chat_enabled: next }
        );
        if (!res.success) {
          throw new Error(res.error || 'Aggiornamento non riuscito');
        }
      } catch (err) {
        // Rollback.
        setPages((prev) => prev.map((p) => (p.pageId === page.pageId ? { ...p, chatEnabled: !next } : p)));
        toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
      } finally {
        setTogglingPageId(null);
      }
    },
    [toast]
  );

  // Toast riassuntivo condiviso tra riallineamento globale e per-account.
  const summarizeRealign = useCallback(
    (r: MetaRealignResponse) => {
      const removed = r.results.reduce((acc, x) => acc + x.pagesDeleted + x.pagesMarkedRemoved, 0);
      const added = r.results.reduce((acc, x) => acc + x.pagesAdded, 0);
      const subFailed = r.results.reduce(
        (acc, x) => acc + x.pageSubscriptions.filter((s) => !s.subscribed).length,
        0
      );
      const parts: string[] = [];
      if (added > 0) parts.push(`${added} nuov${added === 1 ? 'a' : 'e'}`);
      if (removed > 0) parts.push(`${removed} rimoss${removed === 1 ? 'a' : 'e'}`);
      if (r.connectionsError > 0) parts.push(`${r.connectionsError} in errore`);
      if (subFailed > 0) parts.push(`${subFailed} webhook non sottoscritt${subFailed === 1 ? 'o' : 'i'}`);
      toast({
        title: 'Riallineamento completato',
        description: parts.length > 0 ? `Pagine: ${parts.join(', ')}.` : 'Pagine e permessi già allineati.',
        variant: r.connectionsError > 0 || subFailed > 0 ? 'destructive' : undefined,
      });
    },
    [toast]
  );

  const handleRealign = useCallback(async () => {
    setRealigning(true);
    try {
      const res = await apiClient.post<MetaRealignResponse>('/meta/realign', {});
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Riallineamento non riuscito');
      }
      summarizeRealign(res.data);
      await loadStatus();
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setRealigning(false);
    }
  }, [toast, loadStatus, summarizeRealign]);

  // --- Azioni per-account (Task #3208) ---
  const busy = connecting || realigning || rowAction !== null;

  const handleRealignConnection = useCallback(
    async (conn: MetaConnectionStatus) => {
      setRowAction(`${conn.id}:realign`);
      try {
        const res = await apiClient.post<MetaRealignResponse>(
          `/meta/connections/${encodeURIComponent(conn.id)}/realign`,
          {}
        );
        if (!res.success || !res.data) {
          throw new Error(res.error || 'Riallineamento non riuscito');
        }
        summarizeRealign(res.data);
        await loadStatus();
      } catch (err) {
        toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
      } finally {
        setRowAction(null);
      }
    },
    [toast, loadStatus, summarizeRealign]
  );

  const handleRefreshToken = useCallback(
    async (conn: MetaConnectionStatus) => {
      setRowAction(`${conn.id}:token`);
      try {
        const res = await apiClient.post<MetaConnectionRefreshTokenResponse>(
          `/meta/connections/${encodeURIComponent(conn.id)}/refresh-token`,
          {}
        );
        if (!res.success || !res.data) {
          throw new Error(res.error || 'Rinnovo token non riuscito');
        }
        if (res.data.status === 'refreshed') {
          toast({
            title: 'Token rinnovato',
            description: res.data.tokenExpiresAt
              ? `Nuova scadenza: ${formatDate(res.data.tokenExpiresAt)}.`
              : 'Token rinnovato (Meta non riporta una scadenza).',
          });
        } else {
          toast({
            title: 'Rinnovo non riuscito',
            description: res.data.error || 'Meta ha rifiutato il rinnovo del token.',
            variant: 'destructive',
          });
        }
        await loadStatus();
      } catch (err) {
        toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
      } finally {
        setRowAction(null);
      }
    },
    [toast, loadStatus]
  );

  const handleRenameSave = useCallback(async () => {
    if (!renameTarget) return;
    const label = renameValue.trim();
    setRowAction(`${renameTarget.id}:rename`);
    try {
      const res = await apiClient.patch<MetaConnectionLabelResponse>(
        `/meta/connections/${encodeURIComponent(renameTarget.id)}`,
        { display_label: label.length > 0 ? label : null }
      );
      if (!res.success) {
        throw new Error(res.error || 'Salvataggio etichetta non riuscito');
      }
      toast({
        title: 'Etichetta aggiornata',
        description: label.length > 0 ? `Account rinominato in "${label}".` : 'Etichetta rimossa.',
      });
      setRenameTarget(null);
      await loadStatus();
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setRowAction(null);
    }
  }, [renameTarget, renameValue, toast, loadStatus]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setRowAction(`${deleteTarget.id}:delete`);
    try {
      const res = await apiClient.delete<MetaConnectionDeleteResponse>(
        `/meta/connections/${encodeURIComponent(deleteTarget.id)}`
      );
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Eliminazione non riuscita');
      }
      const { pagesDeleted, pagesMarkedRemoved } = res.data;
      const parts: string[] = [];
      if (pagesDeleted > 0) parts.push(`${pagesDeleted} pagin${pagesDeleted === 1 ? 'a eliminata' : 'e eliminate'}`);
      if (pagesMarkedRemoved > 0)
        parts.push(`${pagesMarkedRemoved} con storico conservat${pagesMarkedRemoved === 1 ? 'o' : 'i'} (disattivate)`);
      toast({
        title: 'Account scollegato',
        description: parts.length > 0 ? parts.join(', ') + '.' : 'Nessuna Pagina associata.',
      });
      setDeleteTarget(null);
      await loadStatus();
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setRowAction(null);
    }
  }, [deleteTarget, toast, loadStatus]);

  // Gerarchia Account → Business Manager → Pagine.
  const accountGroups = useMemo<AccountGroup[]>(() => {
    return connections.map((connection) => {
      const connPages = pages.filter((p) => p.connectionIds.includes(connection.id));
      const byBusiness = new Map<string, BusinessGroup>();
      for (const p of connPages) {
        const key = p.businessId ?? '__no_bm__';
        const name = p.businessName ?? 'Senza Business Manager';
        let group = byBusiness.get(key);
        if (!group) {
          group = { businessKey: key, businessName: name, pages: [] };
          byBusiness.set(key, group);
        }
        group.pages.push(p);
      }
      return { connection, businesses: Array.from(byBusiness.values()), pageCount: connPages.length };
    });
  }, [connections, pages]);

  // Auto-espansione una tantum nel caso comune (1 account → espandilo; se ha 1
  // solo BM → espandi anche quello) così i toggle sono subito raggiungibili.
  useEffect(() => {
    if (didInitExpand.current || accountGroups.length === 0) return;
    didInitExpand.current = true;
    if (accountGroups.length === 1) {
      const acct = accountGroups[0]!;
      setExpandedAccounts(new Set([acct.connection.id]));
      if (acct.businesses.length === 1) {
        setExpandedBusinesses(new Set([`${acct.connection.id}::${acct.businesses[0]!.businessKey}`]));
      }
    }
  }, [accountGroups]);

  const toggleAccount = useCallback((connId: string) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(connId)) next.delete(connId);
      else next.add(connId);
      return next;
    });
  }, []);

  const toggleBusiness = useCallback((bizKey: string) => {
    setExpandedBusinesses((prev) => {
      const next = new Set(prev);
      if (next.has(bizKey)) next.delete(bizKey);
      else next.add(bizKey);
      return next;
    });
  }, []);

  const hasActiveConnection = connections.some((c) => c.status === 'connected');
  const enabledPagesCount = pages.filter((p) => p.chatEnabled).length;

  const renderStatusBadge = () => {
    if (loading) {
      return (
        <Badge variant="outline" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Verifica…
        </Badge>
      );
    }
    if (!appAvailable) {
      return <Badge variant="outline">Non configurata</Badge>;
    }
    if (hasActiveConnection) {
      return <Badge className="bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">Attivo</Badge>;
    }
    return <Badge variant="outline">Da configurare</Badge>;
  };

  const renderWebhookBadge = (webhook: MetaPageWebhookStatus) => {
    if (webhook.subscribed) {
      return (
        <Badge variant="outline" className="shrink-0 gap-1 border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/40 dark:text-green-300">
          <CheckCircle className="h-3 w-3" />
          Webhook OK
        </Badge>
      );
    }
    const detail = webhook.error
      ? `Verifica fallita: ${webhook.error}`
      : webhook.missingFields.length > 0
        ? `Campi mancanti: ${webhook.missingFields.join(', ')}`
        : 'La Pagina non riceve i messaggi inbound';
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="shrink-0 gap-1 border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              <AlertTriangle className="h-3 w-3" />
              Webhook mancante
            </Badge>
          </TooltipTrigger>
          <TooltipContent>{detail}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderPagesTable = (group: BusinessGroup, scopes: string[]) => (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Pagina</th>
            <th className="px-3 py-2 text-left font-medium">Instagram</th>
            <th className="px-3 py-2 text-left font-medium">Capability</th>
            <th className="px-3 py-2 text-center font-medium">Chat</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {group.pages.map((page) => {
            const isRemoved = page.removedAt !== null;
            return (
            <tr key={page.id} className={`hover:bg-muted/30 ${isRemoved ? 'opacity-60' : ''}`}>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <Facebook className="h-4 w-4 text-blue-600 shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate font-medium">{page.pageName || page.pageId}</span>
                      {isRemoved && (
                        <Badge variant="outline" className="shrink-0 gap-1 border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                          <AlertTriangle className="h-3 w-3" />
                          Non più amministrata
                        </Badge>
                      )}
                      {!isRemoved && page.webhook && renderWebhookBadge(page.webhook)}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{page.pageId}</div>
                  </div>
                </div>
              </td>
              <td className="px-3 py-2">
                {page.instagramUsername ? (
                  <div className="flex items-center gap-1.5">
                    <Instagram className="h-4 w-4 text-pink-600 shrink-0" />
                    <span className="truncate">@{page.instagramUsername}</span>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  <TooltipProvider delayDuration={200}>
                    {CAPABILITIES.map((cap) => {
                      const granted = scopes.includes(cap.scope);
                      return (
                        <Tooltip key={cap.key}>
                          <TooltipTrigger asChild>
                            <Badge
                              variant="outline"
                              className={`gap-1 ${granted ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900' : 'bg-gray-50 text-gray-400 border-gray-200 dark:bg-gray-900 dark:text-gray-500 dark:border-gray-700'}`}
                            >
                              {cap.key === 'instagram' ? <Instagram className="h-3 w-3" /> : cap.key === 'messenger' ? <MessageCircle className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
                              {cap.label}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            {granted
                              ? `Permesso "${cap.scope}" concesso`
                              : `Permesso "${cap.scope}" non concesso (governato dall'app Meta)`}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </TooltipProvider>
                </div>
              </td>
              <td className="px-3 py-2">
                <div className="flex items-center justify-center">
                  {togglingPageId === page.pageId ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    <Switch
                      checked={page.chatEnabled}
                      disabled={isRemoved}
                      onCheckedChange={(next) => void handleToggleChat(page, next)}
                      aria-label="Attiva per chat"
                    />
                  )}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <Card className="relative flex h-full flex-col transition-all hover:shadow-md">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Facebook className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">Meta Business</CardTitle>
                <div className="flex flex-wrap items-center gap-1 mt-0.5">
                  {renderStatusBadge()}
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col pt-0">
          <p className="text-sm text-muted-foreground mb-4">
            Collega Facebook, Instagram e Business Manager per il tuo tenant.
          </p>

          {hasActiveConnection && (
            <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 mb-3">
              <CheckCircle className="h-3 w-3" />
              <span>
                {pages.length} pagin{pages.length === 1 ? 'a' : 'e'} · {enabledPagesCount} attiv{enabledPagesCount === 1 ? 'a' : 'e'} per chat
              </span>
            </div>
          )}

          <div className="mt-auto flex gap-2">
            <Button
              size="sm"
              className="flex-1"
              onClick={() => setDialogOpen(true)}
            >
              <Settings className="h-4 w-4 mr-1" />
              {pages.length > 0 || connections.length > 0 ? 'Gestisci' : 'Configura'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:!max-w-5xl sm:!max-h-[85vh] sm:!grid-cols-1 sm:!flex sm:!flex-col"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Facebook className="h-5 w-5 text-blue-600" />
              Meta Business
            </DialogTitle>
            <DialogDescription>
              Gestisci le Pagine Facebook/Instagram collegate e attiva i canali chat.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 overflow-y-auto overflow-x-hidden flex-1 min-h-0 pr-2">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
              </div>
            ) : !appAvailable ? (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  App Meta non ancora configurata. L'amministratore deve attivarla dal pannello Master
                  prima di poter collegare gli account.
                </span>
              </div>
            ) : connections.length === 0 ? (
              // Empty-state: nessuna connessione → connect.
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Nessun account Meta collegato. Avvia il consenso per collegare le tue Pagine Facebook e gli account Instagram.
                </p>
                <Button onClick={handleConnect} disabled={connecting}>
                  {connecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Facebook className="mr-2 h-4 w-4" />}
                  Collega account Meta
                </Button>
              </div>
            ) : (
              // Accordion Account → Business Manager → Pagine.
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-muted-foreground">
                    {connections.length} account collegat{connections.length === 1 ? 'o' : 'i'}
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleConnect} disabled={busy}>
                      {connecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
                      Aggiungi utente
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void handleRealign()}
                      disabled={busy}
                      title="Ri-legge Pagine e permessi da Meta sul collegamento esistente (senza nuovo accesso)"
                    >
                      {realigning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCw className="mr-2 h-4 w-4" />}
                      Riallinea
                    </Button>
                  </div>
                </div>

                <div className="overflow-hidden rounded-md border divide-y">
                  {accountGroups.map((acct) => {
                    const conn = acct.connection;
                    const accountExpanded = expandedAccounts.has(conn.id);
                    const isSelf = conn.connectedByUserId === currentUserId;
                    const scopes = conn.grantedScopes ?? [];
                    const tokenExpired = isExpired(conn.tokenExpiresAt);
                    return (
                      <div key={conn.id}>
                        <div className="flex w-full items-center gap-2 px-3 py-2.5 hover:bg-muted/40">
                          <button
                            type="button"
                            onClick={() => toggleAccount(conn.id)}
                            className="flex min-w-0 flex-1 items-center gap-3 text-left"
                            aria-expanded={accountExpanded}
                          >
                            {accountExpanded ? (
                              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                            )}
                            <div className="rounded bg-muted p-1.5 shrink-0">
                              <Facebook className="h-4 w-4 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium">
                                {conn.displayLabel || conn.metaUserName || 'Account Meta'}
                                {conn.displayLabel && conn.metaUserName && (
                                  <span className="ml-1 text-xs font-normal text-muted-foreground">({conn.metaUserName})</span>
                                )}
                                {isSelf && <span className="ml-1 text-xs font-normal text-muted-foreground">(tu)</span>}
                              </div>
                              <div className="truncate text-xs text-muted-foreground">
                                {acct.pageCount} pagin{acct.pageCount === 1 ? 'a' : 'e'} · {acct.businesses.length} business · Token: {formatDate(conn.tokenExpiresAt)}
                              </div>
                            </div>
                          </button>
                          <ConnectionStatusBadges conn={conn} tokenExpired={tokenExpired} />
                          <TooltipProvider delayDuration={200}>
                            <div className="flex shrink-0 items-center gap-0.5">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => void handleRealignConnection(conn)}
                                    disabled={busy}
                                    aria-label="Riallinea account"
                                  >
                                    {rowAction === `${conn.id}:realign` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Riallinea Pagine e permessi da Meta (senza nuovo accesso)</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => void handleRefreshToken(conn)}
                                    disabled={busy}
                                    aria-label="Rinnova token"
                                  >
                                    {rowAction === `${conn.id}:token` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Rinnova il token di accesso long-lived</TooltipContent>
                              </Tooltip>
                              {isSelf && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() => void handleEditPermissions(conn.id)}
                                      disabled={busy}
                                      aria-label="Modifica autorizzazioni"
                                    >
                                      <Settings className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Modifica autorizzazioni: Business, Pagine e permessi condivisi con l'app</TooltipContent>
                                </Tooltip>
                              )}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => {
                                      setRenameTarget(conn);
                                      setRenameValue(conn.displayLabel ?? '');
                                    }}
                                    disabled={busy}
                                    aria-label="Rinomina account"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Rinomina (etichetta personalizzata)</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-red-600 hover:text-red-700 dark:text-red-400"
                                    onClick={() => setDeleteTarget(conn)}
                                    disabled={busy}
                                    aria-label="Elimina account"
                                  >
                                    {rowAction === `${conn.id}:delete` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Scollega l'account (le Pagine con storico restano visibili ma disattivate)</TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                        </div>

                        {accountExpanded && (
                          <div className="space-y-2 bg-muted/20 px-3 pb-3 pt-1">
                            {conn.lastRefreshError && (
                              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
                                Ultimo errore: {conn.lastRefreshError}
                              </div>
                            )}
                            {acct.businesses.length === 0 ? (
                              <div className="rounded-md border bg-background p-3 text-sm text-muted-foreground">
                                Nessuna Pagina trovata per questo account. Verifica i permessi concessi e riprova.
                              </div>
                            ) : (
                              acct.businesses.map((bm) => {
                                const bizKey = `${conn.id}::${bm.businessKey}`;
                                const bizExpanded = expandedBusinesses.has(bizKey);
                                return (
                                  <div key={bizKey} className="rounded-md border bg-background">
                                    <button
                                      type="button"
                                      onClick={() => toggleBusiness(bizKey)}
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/30"
                                      aria-expanded={bizExpanded}
                                    >
                                      {bizExpanded ? (
                                        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                      ) : (
                                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                      )}
                                      <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                      <span className="truncate text-sm font-medium">{bm.businessName}</span>
                                      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                                        {bm.pages.length} pagin{bm.pages.length === 1 ? 'a' : 'e'}
                                      </span>
                                    </button>
                                    {bizExpanded && (
                                      <div className="px-2 pb-2">{renderPagesTable(bm, scopes)}</div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog rinomina (etichetta personalizzata) */}
      <Dialog open={renameTarget !== null} onOpenChange={(open) => { if (!open) setRenameTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rinomina account</DialogTitle>
            <DialogDescription>
              Etichetta personalizzata per distinguere account con lo stesso nome
              {renameTarget?.metaUserName ? ` (account Meta: ${renameTarget.metaUserName})` : ''}. Lascia vuoto per rimuoverla.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder={renameTarget?.metaUserName ?? 'Etichetta'}
            maxLength={120}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleRenameSave();
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)} disabled={rowAction !== null}>
              Annulla
            </Button>
            <Button onClick={() => void handleRenameSave()} disabled={rowAction !== null}>
              {renameTarget && rowAction === `${renameTarget.id}:rename` && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione connessione */}
      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Scollega account Meta</DialogTitle>
            <DialogDescription>
              Stai per scollegare {deleteTarget?.displayLabel || deleteTarget?.metaUserName || 'questo account'}.
              Le Pagine non più amministrate spariranno dalle configurazioni Messenger e Instagram.
              Le conversazioni esistenti resteranno disponibili in sola lettura. L'operazione non è reversibile:
              per ricollegare l'account servirà un nuovo accesso Meta.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={rowAction !== null}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={() => void handleDeleteConfirm()} disabled={rowAction !== null}>
              {deleteTarget && rowAction === `${deleteTarget.id}:delete` && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Scollega
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
